import { useState, useMemo } from "react";
import { Link, useParams } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { ArrowLeft, MapPin, Globe, Phone, Clock, Pencil, Check, X, Lock, Flag, XCircle, CalendarX, Info, DollarSign, ParkingCircle, AlertTriangle, Bell, BellOff, Repeat } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DanceStyleBadge, EventStyleBadges } from "../components/calendar/DanceStyleBadge";
import { formatTimeRange, formatShortDate } from "../lib/dateUtils";
import { groupIntoSeries, seriesLabel, countThrough, isoMonthsFromNow } from "../lib/series";
import type { SeriesEvent } from "../lib/series";
import { useAdminMode } from "../contexts/AdminModeContext";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useFavorites } from "../hooks/useFavorites";
import { cn } from "../lib/utils";
import type { Id } from "convex/_generated/dataModel";

type NoticeType = "closure" | "cancellation" | "notice" | "fee_update" | "parking";

const NOTICE_TYPE_CONFIG: Record<NoticeType, {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  className: string;
}> = {
  closure: { icon: XCircle, label: "Closure", className: "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200" },
  cancellation: { icon: CalendarX, label: "Cancellation", className: "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200" },
  notice: { icon: Info, label: "Notice", className: "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200" },
  fee_update: { icon: DollarSign, label: "Fee Update", className: "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200" },
  parking: { icon: ParkingCircle, label: "Parking", className: "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200" },
};

type VenueEvent = SeriesEvent & {
  title: string;
  startTime: string;
  endTime?: string;
  danceStyles: string[];
  mainStyles?: string[];
  sourceLinks?: { label: string; url: string }[];
};

export function VenuePage() {
  const params = useParams<{ id: string }>();
  const venueId = params.id as Id<"venues">;
  const { isAdminMode, showAdminControls } = useAdminMode();

  const { user } = useCurrentUser();
  const isStudioOwner = user?.accountType === "studio";

  const venue = useQuery(api.venues.getVenue, { venueId });
  const events = useQuery(api.venues.getEventsAtVenue, { venueId });

  /**
   * One row per series, not per occurrence.
   *
   * A venue running four weekly dances was listing a hundred rows — the same
   * five nights over and over, two years deep — because every generated
   * occurrence got its own entry. The list and the map were fixed for this;
   * the venue page was missed.
   */
  const venueSeries = useMemo(
    () => (events ? groupIntoSeries<VenueEvent>(events) : []),
    [events]
  );
  const activeNotices = useQuery(api.venueNotices.getNoticesForVenue, { venueId });
  const updateVenue = useMutation(api.venues.updateVenue);
  const claimVenue = useMutation(api.venues.claimVenue);

  const { isVenueFavorited, toggleVenueFavorite, canFavorite } = useFavorites();
  const isFollowing = isVenueFavorited(venueId);
  const [followBusy, setFollowBusy] = useState(false);

  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    address: "",
    website: "",
    phone: "",
    description: "",
    regularSchedule: "",
  });

  function startEdit() {
    if (!venue) return;
    setForm({
      name: venue.name ?? "",
      address: venue.address ?? "",
      website: venue.website ?? "",
      phone: venue.phone ?? "",
      description: venue.description ?? "",
      regularSchedule: venue.regularSchedule ?? "",
    });
    setSaveError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setSaveError(null);
  }

  async function saveEdit() {
    if (!venue) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateVenue({
        venueId: venue._id,
        name: form.name || undefined,
        address: form.address || undefined,
        website: form.website || undefined,
        phone: form.phone || undefined,
        description: form.description || undefined,
        regularSchedule: form.regularSchedule || undefined,
      });
      setEditing(false);
    } catch (e) {
      setSaveError(String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleClaim() {
    if (!venue) return;
    setClaiming(true);
    setClaimError(null);
    try {
      await claimVenue({ venueId: venue._id });
      setClaimed(true);
    } catch (e: any) {
      setClaimError(e.message ?? "Failed to claim venue.");
    } finally {
      setClaiming(false);
    }
  }

  if (venue === undefined) {
    return (
      <PageContainer>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </PageContainer>
    );
  }

  if (!venue) {
    return (
      <PageContainer>
        <div className="py-12 text-center">
          <p className="text-muted-foreground mb-4">Venue not found.</p>
          <Button asChild variant="outline">
            <Link href="/events">Go Home</Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/events">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </Button>
      </div>

      {/* Venue header */}
      <div className="bg-card rounded-xl border border-border/50 p-6 mb-6">
        {editing ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Venue name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Address</label>
              <Input
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Address"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Website</label>
              <Input
                value={form.website}
                onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Phone</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Phone number"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Description"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Regular Schedule</label>
              <textarea
                value={form.regularSchedule}
                onChange={(e) => setForm((f) => ({ ...f, regularSchedule: e.target.value }))}
                placeholder="e.g. Sundays 7–10pm"
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              />
            </div>
            {saveError && <p className="text-sm text-red-500">{saveError}</p>}
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={saveEdit} disabled={saving}>
                <Check className="w-4 h-4" />
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>
                <X className="w-4 h-4" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-2">
              <h1 className="font-display text-3xl text-foreground">{venue.name}</h1>
              <div className="flex items-center gap-2 shrink-0">
                {showAdminControls && venue.claimStatus !== "reviewing" && (
                  <Button size="sm" variant="outline" onClick={startEdit}>
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Button>
                )}
                {showAdminControls && venue.claimStatus === "reviewing" && (
                  <span
                    title="This venue is currently under review and cannot be edited."
                    className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800 cursor-default"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Under Review
                  </span>
                )}
                {!showAdminControls && isStudioOwner && venue.claimStatus === "unclaimed" && !claimed && (
                  <div className="flex flex-col items-end gap-1">
                    <Button size="sm" variant="outline" onClick={handleClaim} disabled={claiming}>
                      <Flag className="w-4 h-4" />
                      {claiming ? "Claiming..." : "Claim this venue"}
                    </Button>
                    {claimError && (
                      <p className="text-xs text-destructive">{claimError}</p>
                    )}
                  </div>
                )}
                {claimed && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800">
                    Claim submitted — pending review
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <MapPin className="w-4 h-4" />
              <span>{venue.address}</span>
            </div>

            {venue.description && (
              <p className="text-foreground/80 mb-4">{venue.description}</p>
            )}

            <div className="flex flex-wrap gap-3">
              {venue.website && (
                <a
                  href={venue.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <Globe className="w-4 h-4" />
                  Website
                </a>
              )}
              {venue.phone && (
                <a
                  href={`tel:${venue.phone}`}
                  className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  {venue.phone}
                </a>
              )}
            </div>

            {venue.danceStyles && venue.danceStyles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {venue.danceStyles.map((style) => (
                  <DanceStyleBadge key={style} slug={style} />
                ))}
              </div>
            )}

            {venue.regularSchedule && (
              <div className="mt-4 p-3 bg-secondary/30 rounded-lg">
                <h3 className="text-xs font-semibold text-muted-foreground mb-1">Regular Schedule</h3>
                <p className="text-sm text-foreground whitespace-pre-wrap">{venue.regularSchedule}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Follow — venue notices by email. Signed-out visitors are pointed at
          sign-in rather than shown a control that cannot work for them. */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        {canFavorite ? (
          <Button
            variant={isFollowing ? "outline" : "default"}
            size="sm"
            disabled={followBusy}
            onClick={async () => {
              setFollowBusy(true);
              try {
                await toggleVenueFavorite(venueId, venue.name);
              } finally {
                setFollowBusy(false);
              }
            }}
          >
            {isFollowing ? (
              <BellOff className="w-4 h-4" />
            ) : (
              <Bell className="w-4 h-4" />
            )}
            {followBusy ? "..." : isFollowing ? "Following" : "Follow this venue"}
          </Button>
        ) : (
          <Button variant="outline" size="sm" asChild>
            <Link href="/login">
              <Bell className="w-4 h-4" />
              Sign in to follow this venue
            </Link>
          </Button>
        )}
        <p className="text-xs text-muted-foreground">
          {isFollowing
            ? "We'll email you when this venue posts a closure, cancellation or other notice."
            : "Get an email when this venue posts a closure, cancellation or other notice."}
        </p>
      </div>

      {/* Active notices */}
      {activeNotices && activeNotices.length > 0 && (
        <div className="space-y-3 mb-6">
          {activeNotices.map((notice) => {
            const config = NOTICE_TYPE_CONFIG[notice.type as NoticeType];
            const Icon = config?.icon ?? AlertTriangle;
            return (
              <div
                key={notice._id}
                className={cn("rounded-xl border px-4 py-3 flex items-start gap-3", config?.className)}
              >
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{notice.title}</p>
                  {notice.body && (
                    <p className="text-sm mt-0.5 opacity-80 whitespace-pre-wrap">{notice.body}</p>
                  )}
                  {(notice.startDate || notice.endDate) && (
                    <p className="text-xs mt-1 opacity-60">
                      {notice.startDate && notice.endDate
                        ? `${notice.startDate} – ${notice.endDate}`
                        : notice.startDate
                        ? `From ${notice.startDate}`
                        : `Until ${notice.endDate}`}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upcoming events */}
      <section>
        <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Upcoming Events
        </h2>

        {events === undefined ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 bg-secondary/30 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : venueSeries.length === 0 ? (
          <div className="text-center py-8 bg-card rounded-xl border border-border/50">
            <p className="text-muted-foreground">No upcoming events at this venue.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {venueSeries.map((series) => {
              const event = series.next;
              return (
              <div
                key={series.key}
                className="bg-card rounded-xl border border-border/50 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-medium text-foreground">{event.title}</h3>
                  {series.isRecurring && (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                      <Repeat className="w-3 h-3 shrink-0" />
                      <span className="whitespace-nowrap">{seriesLabel(series)}</span>
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatShortDate(event.date)}
                  {" · "}
                  {formatTimeRange(event.startTime, event.endTime)}
                </p>
                {series.isRecurring && (
                  <p className="text-xs text-muted-foreground/80 mt-0.5">
                    Next of {countThrough(series, isoMonthsFromNow(3))} dates in
                    the next 3 months
                  </p>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  <EventStyleBadges danceStyles={event.danceStyles} mainStyles={event.mainStyles} />
                </div>
                {event.sourceLinks && event.sourceLinks.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {event.sourceLinks.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </section>
    </PageContainer>
  );
}
